/**
 * Encroachment Imagery Comparison Service
 * Handles API calls for satellite image comparison of encroachment alerts
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class EncroachmentImageryService {
  /**
   * Check service health
   */
  async checkHealth() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/encroachment/imagery/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Health check failed:', error);
      throw new Error(error.message || 'Failed to check service health');
    }
  }

  /**
   * Check if imagery is available for an encroachment alert
   * @param {number} latitude - Alert latitude
   * @param {number} longitude - Alert longitude
   * @param {string} alertDate - Alert date (YYYY-MM-DD)
   * @returns {Promise<Object>} Availability status
   */
  async checkImageryAvailability(latitude, longitude, alertDate) {
    try {
      console.log(`🔍 Checking imagery availability for alert on ${alertDate}`);
      
      const response = await fetch(
        `${API_BASE_URL}/api/encroachment/imagery/check-availability`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            latitude,
            longitude,
            alert_date: alertDate
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.availability;
    } catch (error) {
      console.error('❌ Availability check failed:', error);
      throw new Error(error.message || 'Failed to check imagery availability');
    }
  }

  /**
   * Get comparison images for an encroachment alert
   * @param {number} latitude - Alert latitude
   * @param {number} longitude - Alert longitude
   * @param {string} alertDate - Alert date (YYYY-MM-DD)
   * @param {number} bboxSize - Bounding box size (default: 0.05)
   * @returns {Promise<Object>} Comparison data with before/after images
   */
  async getComparisonImages(latitude, longitude, alertDate, bboxSize = 0.05) {
    try {
      console.log(`📊 Fetching comparison images for alert on ${alertDate}`);
      
      const response = await fetch(
        `${API_BASE_URL}/api/encroachment/imagery/compare`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            latitude,
            longitude,
            alert_date: alertDate,
            bbox_size: bboxSize
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Comparison images received');
      return data.comparison;
    } catch (error) {
      console.error('❌ Failed to get comparison images:', error);
      throw new Error(error.message || 'Failed to retrieve comparison images');
    }
  }

  /**
   * Analyze images using Gemini AI - Segmentation Approach
   * @param {Object} comparisonData - Comparison data with images
   * @returns {Promise<Object>} AI segmentation analysis results
   */
  async analyzeSegmentation(comparisonData) {
    try {
      console.log('🤖 Starting AI Segmentation Analysis...');
      
      const requestPayload = {
        image1_base64: comparisonData.image1.preview_base64,
        image2_base64: comparisonData.image2.preview_base64,
        latitude: comparisonData.location.latitude,
        longitude: comparisonData.location.longitude,
        alert_date: comparisonData.encroachment_context.alert_date,
        before_date: comparisonData.image1.actual_date,
        after_date: comparisonData.image2.actual_date,
        days_between: this._calculateDaysBetween(
          comparisonData.image1.actual_date,
          comparisonData.image2.actual_date
        )
      };
      
      const response = await fetch(
        `${API_BASE_URL}/api/encroachment/imagery/analyze-segmentation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestPayload),
          signal: AbortSignal.timeout(180000) // 3 minutes timeout for AI analysis
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ AI Segmentation Analysis complete');
      return data.analysis;
    } catch (error) {
      console.error('❌ AI Segmentation Analysis failed:', error);
      throw new Error(error.message || 'Failed to perform segmentation AI analysis');
    }
  }

  /**
   * Calculate days between two dates
   * @param {string} date1 - First date (YYYY-MM-DD)
   * @param {string} date2 - Second date (YYYY-MM-DD)
   * @returns {number} Days between dates
   */
  _calculateDaysBetween(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Format date for display
   * @param {string} dateString - Date string (YYYY-MM-DD)
   * @returns {string} Formatted date
   */
  formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
}

export default new EncroachmentImageryService();

